import { storeToRefs } from 'pinia';
import { computed, readonly, ref } from 'vue';

import { useLocalStorage } from '@/composables/useLocalStorage';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { usePlatformStore } from '@/stores/platform';

const RATE_LIMIT_DURATION_SECONDS = 60 * 60; // one hour

const currentUnixTimestampSeconds = ref(Math.floor(Date.now() / 1000));
setInterval(() => {
  currentUnixTimestampSeconds.value = Math.floor(Date.now() / 1000);
}, 1000);

export function useIGCMStatus() {
  const { currentBrand } = storeToRefs(useIdentityStore());
  const { flags } = storeToRefs(useFlagStore());
  const platformStore = usePlatformStore();

  const timeWhenRateLimitBeganByBrandId = useLocalStorage(
    `igcm-rate-limit-began-by-brand-id`,
    {},
    { listenToStorageChanges: true },
  );
  const timeWhenRateLimitBegan = computed({
    get() {
      return timeWhenRateLimitBeganByBrandId.value[currentBrand.value?.id] ?? null;
    },
    set(newValue) {
      if (currentBrand.value?.id) {
        timeWhenRateLimitBeganByBrandId.value = {
          ...timeWhenRateLimitBeganByBrandId.value,
          [currentBrand.value.id]: newValue,
        };
      }
    },
  });

  const enrollmentStatusByBrandId = useLocalStorage(
    `igcm-enrollment-status-by-brand-id`,
    {},
    { listenToStorageChanges: true },
  );
  const isEnrolledInIGCM = computed({
    get() {
      return enrollmentStatusByBrandId.value[currentBrand.value?.id] ?? false;
    },
    set(newValue) {
      if (currentBrand.value?.id) {
        enrollmentStatusByBrandId.value = {
          ...enrollmentStatusByBrandId.value,
          [currentBrand.value.id]: newValue,
        };
      }
    },
  });

  const isIGCMRateLimited = computed(
    () =>
      !!timeWhenRateLimitBegan.value &&
      currentUnixTimestampSeconds.value - timeWhenRateLimitBegan.value <
        RATE_LIMIT_DURATION_SECONDS,
  );

  const secondsRemainingForIGCMRateLimit = computed(() =>
    timeWhenRateLimitBegan.value
      ? Math.max(
          0,
          RATE_LIMIT_DURATION_SECONDS -
            (currentUnixTimestampSeconds.value - timeWhenRateLimitBegan.value),
        )
      : 0,
  );

  const formattedIGCMRateLimitCountdownTimer = computed(() => {
    const minutes = Math.floor(secondsRemainingForIGCMRateLimit.value / 60);
    const seconds = `${secondsRemainingForIGCMRateLimit.value % 60}`.padStart(2, '0');
    return `${minutes}:${seconds}`;
  });

  const enableIGCMFeatures = computed(
    () =>
      flags.value.igCmDiscovery &&
      platformStore.hasInstagramCreatorMarketplaceScopes &&
      isEnrolledInIGCM.value,
  );

  function setIGCMRateLimitStatusFromResponse(response, clearOnExternalApiUsed = false) {
    const headers = response.headers || {};
    const headerIndicatesRateLimit = headers['x-data-source-reason'] === 'external-api-rate-limit';
    const headerIndicatesExternalApiUsed = headers['x-data-source'] === 'external-api';
    const statusIndicatesRateLimit = response.status === 429;

    if ((headerIndicatesRateLimit || statusIndicatesRateLimit) && !isIGCMRateLimited.value) {
      timeWhenRateLimitBegan.value = currentUnixTimestampSeconds.value;
    } else if (headerIndicatesExternalApiUsed && clearOnExternalApiUsed) {
      timeWhenRateLimitBegan.value = null;
    }

    // Track enrollment status
    const headerIndicatesEnrollmentRequired =
      headers['x-data-source-reason'] === 'enrolment-required';

    const statusIndicatesEnrollmentRequired =
      response.status === 403 && response.data?.reason === 'enrolment-required';

    if (statusIndicatesEnrollmentRequired || headerIndicatesEnrollmentRequired) {
      isEnrolledInIGCM.value = false;
    } else if (
      headerIndicatesExternalApiUsed ||
      headerIndicatesRateLimit ||
      statusIndicatesRateLimit
    ) {
      isEnrolledInIGCM.value = true;
    }
  }

  async function setIGCStatusFromAxiosCall(promise, clearOnExternalApiUsed = false) {
    try {
      const response = await promise;
      setIGCMRateLimitStatusFromResponse(response, clearOnExternalApiUsed);
      return response;
    } catch (e) {
      if (e.response) {
        setIGCMRateLimitStatusFromResponse(e.response);
      }
      throw e;
    }
  }

  return {
    isIGCMRateLimited,
    secondsRemainingForIGCMRateLimit,
    setIGCStatusFromAxiosCall,
    formattedIGCMRateLimitCountdownTimer,
    isEnrolledInIGCM: readonly(isEnrolledInIGCM),
    enableIGCMFeatures,
  };
}
